import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import CrudTable from "../components/CrudTable";
import MonthlyReadings from "../components/MonthlyReadings";
import PDFButton from "../components/PDFButton";
import WarehouseStatusPanel from "../components/WarehouseStatusPanel";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

const MAIN_TABS = [
  { key: "RP", label: "RP" },
  { key: "RME", label: "RME" },
  { key: "RSU", label: "RSU" },
  { key: "FOSAS", label: "Aguas Residuales" },
  { key: "VAL", label: "Valorización" },
  { key: "WHS", label: "Estatus de Almacenes" },
];

const SUB_TABS = [
  { key: "waste_requests", label: "Solicitudes" },
  { key: "waste_weighings", label: "Registro de Pesado" },
  { key: "waste_logs", label: "Bitácora" },
  { key: "waste_manifests", label: "Manifiestos" },
  { key: "waste_disposals", label: "Reportes de Disposición" },
  { key: "waste_providers_legacy", label: "Proveedores Aprobados" },
  { key: "waste_procedures", label: "Procedimientos" },
  { key: "waste_coas", label: "COA" },
];

export default function Waste() {
  const [tab, setTab] = useState("RP");
  const [sub, setSub] = useState("waste_weighings");

  return (
    <div data-testid="waste-module">
      <div className="flex items-center justify-between mb-3">
        <h1 className="card-h1" style={{ margin: 0 }}>Gestión de Residuos</h1>
        <PDFButton endpoint="/pdf/module/racs" filename="reporte_residuos.pdf" label="Reporte PDF" />
      </div>

      <div className="tabs-bar">
        {MAIN_TABS.map((t) => (
          <button key={t.key} className={`tab ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)} data-testid={`waste-main-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "WHS" ? <WarehouseStatusPanel /> : tab === "VAL" ? <Valorization /> : tab === "FOSAS" ? <Fosas /> : (
        <>
          <WastePricePanel type={tab} />
          <div className="tabs-bar" style={{ marginBottom: 16 }}>
            {SUB_TABS.map((s) => (
              <button key={s.key} className={`tab ${sub === s.key ? "active" : ""}`} onClick={() => setSub(s.key)} data-testid={`waste-sub-${s.key}`}>
                {s.label}
              </button>
            ))}
          </div>
          <SubTable collection={sub} type={tab} />
        </>
      )}
    </div>
  );
}

function WastePricePanel({ type }) {
  const [price, setPrice] = useState(0);
  const [priceLoaded, setPriceLoaded] = useState(false);
  const [totals, setTotals] = useState({ kg: 0, cost: 0 });
  const monthLbl = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
  const [chartData, setChartData] = useState([]);

  async function loadPrice() {
    try {
      const { data } = await api.get("/data/tenant_config", { params: { key: `waste_price_per_kg_${type}` } });
      if (data.length) setPrice(Number(data[0].value) || 0);
    } catch { /* ignore */ }
    setPriceLoaded(true);
  }

  async function loadTotals() {
    try {
      const { data } = await api.get("/data/waste_weighings", { params: { type } });
      const year = new Date().getFullYear();
      let kg = 0, cost = 0;
      const monthly = Array.from({ length: 12 }, () => ({ kg: 0, cost: 0 }));
      for (const w of data) {
        if (!w.date) continue;
        const d = new Date(w.date);
        if (d.getFullYear() !== year) continue;
        const m = d.getMonth();
        const k = Number(w.weight_kg || 0);
        const c = Number(w.cost) || (k * price);
        kg += k; cost += c;
        monthly[m].kg += k; monthly[m].cost += c;
      }
      setTotals({ kg, cost });
      setChartData(monthLbl.map((m, i) => ({ month: m, kg: monthly[i].kg, costo: monthly[i].cost })));
    } catch (e) { console.error(e); }
  }

  useEffect(() => { loadPrice(); /* eslint-disable-next-line */ }, [type]);
  useEffect(() => { if (priceLoaded) loadTotals(); /* eslint-disable-next-line */ }, [type, price, priceLoaded]);

  async function savePrice(v) {
    const n = Number(v) || 0;
    setPrice(n);
    try {
      const { data: existing } = await api.get("/data/tenant_config", { params: { key: `waste_price_per_kg_${type}` } });
      if (existing.length) await api.patch(`/data/tenant_config/${existing[0].id}`, { value: n });
      else await api.post("/data/tenant_config", { key: `waste_price_per_kg_${type}`, value: n });
    } catch (e) { console.error(e); }
  }

  return (
    <div className="card mb-3" style={{ background: "rgba(29,158,117,0.06)" }} data-testid={`waste-price-panel-${type}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <label className="label" style={{ margin: 0 }}>Precio por KG ($MXP) {type}:</label>
        <input
          type="number"
          step="any"
          className="input"
          style={{ width: 140 }}
          value={priceLoaded ? price : ""}
          onChange={(e) => setPrice(Number(e.target.value) || 0)}
          onBlur={(e) => savePrice(e.target.value)}
          data-testid={`waste-price-${type}`}
        />
        <span className="text-xs text-dim">El costo de cada pesaje se calcula como Volumen (KG) × Precio. Editar el costo manualmente lo sobrescribe.</span>
        <div className="flex-1" />
        <span className="badge badge-green">Volumen YTD: {totals.kg.toLocaleString("es-MX", { maximumFractionDigits: 1 })} kg</span>
        <span className="badge" style={{ background: "var(--accent)", color: "#fff" }}>Costo YTD: ${totals.cost.toLocaleString("es-MX", { maximumFractionDigits: 2 })}</span>
      </div>
      {chartData.some((d) => d.kg > 0 || d.costo > 0) && (
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={chartData}>
            <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
            <XAxis dataKey="month" stroke="var(--text-dim)" fontSize={11} interval={0} />
            <YAxis yAxisId="left" stroke="var(--text-dim)" fontSize={11} />
            <YAxis yAxisId="right" orientation="right" stroke="var(--text-dim)" fontSize={11} />
            <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-2)" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar yAxisId="left" dataKey="kg" fill="#1d9e75" name="Volumen (kg)" />
            <Bar yAxisId="right" dataKey="costo" fill="#e74c3c" name="Costo ($MXP)" />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function SubTable({ collection, type }) {
  const fieldsByColl = {
    waste_requests: [
      { key: "code", label: "# Solicitud" },
      { key: "requested_by", label: "Solicitante" },
      { key: "waste_type", label: "Tipo de residuo", default: type },
      { key: "waste_code", label: "Clave" },
      { key: "quantity_kg", label: "Cantidad (kg)", type: "number" },
      { key: "container", label: "Tipo de envase" },
      { key: "movement", label: "Movimiento", type: "select", options: ["Ingreso", "Retiro", "Solped"] },
      { key: "date", label: "Fecha", type: "date" },
      { key: "status", label: "Status", type: "select", options: ["Abierta", "En Proceso", "Atendida"] },
      { key: "received_by", label: "Recibe" },
    ],
    waste_weighings: [
      { key: "date", label: "Fecha", type: "date", required: true },
      { key: "description", label: "Descripción del residuo", full: true },
      { key: "weight_kg", label: "Peso (kg)", type: "number", required: true },
      { key: "cost", label: "Costo ($MXP, auto)", type: "number" },
      { key: "area", label: "Patio/Área" },
      { key: "received_by", label: "Recibe" },
      { key: "type", label: "Tipo", default: type, hiddenInForm: true, hidden: true },
      { key: "movement", label: "Movimiento", type: "select", options: ["Disposicion", "Reciclaje"] },
    ],
    waste_logs: [
      { key: "code", label: "# Bitácora" },
      { key: "month", label: "Mes", type: "number" },
      { key: "year", label: "Año", type: "number" },
      { key: "total_kg", label: "Volumen total (kg)", type: "number" },
      { key: "cost", label: "Costo $", type: "number" },
      { key: "providers", label: "Proveedores" },
      { key: "validated_by", label: "Validado por" },
      { key: "type", label: "Tipo", default: type, hiddenInForm: true, hidden: true },
    ],
    waste_manifests: [
      { key: "code", label: "# Manifiesto", required: true },
      { key: "date", label: "Fecha", type: "date" },
      { key: "waste_type", label: "Tipo de residuo", default: type },
      { key: "provider", label: "Proveedor" },
      { key: "delivered_by", label: "Entregado por" },
      { key: "received_by", label: "Recibido por" },
      { key: "service_report", label: "Reporte de servicio" },
      { key: "location", label: "Ubicación física" },
    ],
    waste_disposals: [
      { key: "folio", label: "Folio", required: true },
      { key: "date", label: "Fecha", type: "date" },
      { key: "waste_type", label: "Tipo residuo", default: type },
      { key: "provider", label: "Proveedor" },
      { key: "received_by", label: "Recibido por" },
    ],
    waste_providers_legacy: [
      { key: "name", label: "Nombre", required: true },
      { key: "auth_collection", label: "# Autorización recolección" },
      { key: "auth_disposal", label: "# Autorización disposición" },
      { key: "management_plan", label: "Plan de manejo" },
      { key: "contract", label: "# Contrato" },
      { key: "approved_operators", label: "Operadores aprobados" },
      { key: "contact", label: "Contacto" },
      { key: "phone", label: "# Contacto" },
    ],
    waste_procedures: [
      { key: "name", label: "Nombre", required: true },
      { key: "version", label: "Versión" },
      { key: "last_update", label: "Última actualización", type: "date" },
      { key: "drive_url", label: "URL Drive" },
    ],
    waste_coas: [
      { key: "year", label: "Año", type: "number", required: true },
      { key: "presented_by", label: "Presentado por" },
      { key: "legal_rep", label: "Representante legal" },
      { key: "presentation_date", label: "Fecha presentación", type: "date" },
    ],
  };

  return (
    <CrudTable
      collection={collection}
      filters={collection === "waste_requests" || collection === "waste_weighings" || collection === "waste_logs" || collection === "waste_manifests" || collection === "waste_disposals" ? { type } : {}}
      testIdPrefix={`${collection}-${type}`}
      fields={fieldsByColl[collection] || []}
    />
  );
}

function Valorization() {
  const [sub, setSub] = useState("Reutilizacion");
  const subs = ["Reutilizacion", "Donacion", "Venta"];
  return (
    <div>
      <div className="tabs-bar">
        {subs.map((s) => (
          <button key={s} className={`tab ${sub === s ? "active" : ""}`} onClick={() => setSub(s)}>{s}</button>
        ))}
      </div>
      <CrudTable
        collection="valorization"
        filters={{ category: sub }}
        testIdPrefix={`val-${sub}`}
        fields={[
          { key: "description", label: "Descripción del material", required: true, full: true },
          { key: "waste_type", label: "Tipo residuo" },
          { key: "quantity_kg", label: "Cantidad (kg)", type: "number" },
          { key: "non_contaminating", label: "Validación no contaminante", type: "checkbox" },
          { key: "use_activity", label: "Actividad de uso" },
          { key: "in_management_plan", label: "Descrito en plan de manejo", type: "checkbox" },
          { key: "amount", label: "Monto $", type: "number" },
          { key: "category", label: "Categoría", default: sub, hiddenInForm: true, hidden: true },
        ]}
      />
    </div>
  );
}

const WASTEWATER_TYPES = [
  { key: "PTAR",       label: "PTAR",            color: "#4A90E2" },
  { key: "TRINCHERAS", label: "Trincheras",      color: "#2EBA7E" },
  { key: "FOSAS",      label: "Fosas Sépticas",  color: "#9B59B6" },
  { key: "LAVADO",     label: "Área de Lavado",  color: "#FF8A65" },
  { key: "PH",         label: "Aguas pH",        color: "#F5B82E" },
  { key: "OTRA",       label: "Otra",            color: "#94A4C0" },
];

function Fosas() {
  const [subtype, setSubtype] = useState("PTAR");
  const meta = WASTEWATER_TYPES.find((t) => t.key === subtype) || WASTEWATER_TYPES[0];
  const year = new Date().getFullYear();
  const [reloadTick, setReloadTick] = useState(0);
  const [importing, setImporting] = useState(false);
  const MONTH_KEYS = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];

  async function importFromEvents() {
    if (!confirm(`Importar volúmenes de los eventos individuales de ${meta.label} ${year} y SUMARLOS al grid mensual?`)) return;
    setImporting(true);
    try {
      const { data: events } = await api.get("/data/waste_weighings", {
        params: { category: "wastewater", type: subtype },
      });
      const monthly = Array(12).fill(0);
      (events || []).forEach((e) => {
        if (!e.date) return;
        const d = new Date(e.date);
        if (d.getFullYear() !== year) return;
        monthly[d.getMonth()] += Number(e.weight_kg || e.volume_m3 || 0);
      });

      const { data: existing } = await api.get("/data/wastewater_readings", { params: { type: subtype } });
      const row = (existing || []).find((r) => r.year === year);
      const payload = { type: subtype, year };
      MONTH_KEYS.forEach((mk, i) => {
        payload[`volume_${mk}`] = Number((row?.[`volume_${mk}`] || 0)) + monthly[i];
      });
      if (row?.id) {
        await api.patch(`/data/wastewater_readings/${row.id}`, payload);
      } else {
        await api.post("/data/wastewater_readings", payload);
      }
      setReloadTick((t) => t + 1);
      alert(`Importados ${monthly.reduce((s, v) => s + v, 0).toLocaleString("es-MX")} M³ desde eventos.`);
    } catch (e) {
      alert("Error importando: " + e.message);
    } finally { setImporting(false); }
  }

  return (
    <div data-testid="wastewater-section">
      <div className="card mb-3" style={{ background: "rgba(52,152,219,0.06)" }}>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="label" style={{ margin: 0 }}>Tipo de agua residual:</label>
          <select className="select" value={subtype} onChange={(e) => setSubtype(e.target.value)} data-testid="wastewater-subtype" style={{ minWidth: 200 }}>
            {WASTEWATER_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <button
            className="btn btn-sm"
            onClick={importFromEvents}
            disabled={importing}
            data-testid={`wastewater-import-events-${subtype}`}
            title="Suma los volúmenes de los eventos individuales del año actual y los agrega al grid mensual"
          >
            {importing ? <span className="spinner" /> : "Importar desde eventos"}
          </button>
          <span className="text-xs text-dim">Cada tipo lleva su propio volumen mensual editable, gráfica e histórico. Click en cualquier celda para editar; "Guardar" para persistir.</span>
        </div>
      </div>

      <div className="card mb-3" data-testid={`wastewater-grid-${subtype}`}>
        <MonthlyReadings
          key={`${subtype}-${reloadTick}`}
          collection="wastewater_readings"
          title={`Volumen mensual editable — ${meta.label}`}
          filters={{ type: subtype }}
          numericKeys={[{ key: "volume", label: `Volumen ${meta.label} (M³)` }]}
          chart={{
            title: `Comportamiento Mensual — ${meta.label}`,
            series: [{ key: "volume", label: `${meta.label} (M³)`, color: meta.color }],
          }}
          headerCards={(row, ytd) => {
            const yt = Number(ytd?.volume || 0);
            const yr = row?.year || year;
            return [
              { label: `YTD ${yr}`, value: `${yt.toLocaleString("es-MX", { maximumFractionDigits: 2 })} M³` },
              { label: "Promedio mensual", value: `${(yt / 12).toLocaleString("es-MX", { maximumFractionDigits: 2 })} M³` },
              { label: "Tipo", value: meta.label, sub: meta.key },
            ];
          }}
        />
      </div>

      <div className="card mb-3" data-testid={`wastewater-events-${subtype}`}>
        <div className="card-title">Eventos / registros individuales — {meta.label}</div>
        <p className="text-xs text-dim mb-3">Bitácora de eventos puntuales (descargas, vaciados, recolecciones). Click en una fila para previsualizar adjuntos. Estos registros son independientes del volumen mensual de arriba.</p>
        <CrudTable
          key={subtype}
          collection="waste_weighings"
          filters={{ type: subtype, category: "wastewater" }}
          testIdPrefix={`wastewater-${subtype}`}
          fields={[
            { key: "date", label: "Fecha", type: "date", required: true },
            { key: "description", label: "Descripción", full: true },
            { key: "weight_kg", label: "Volumen (M³)", type: "number", required: true },
            { key: "area", label: "Ubicación" },
            { key: "received_by", label: "Recibe / Reporta" },
            { key: "cost", label: "Costo ($MXP, opcional)", type: "number" },
            { key: "type", label: "Tipo", default: subtype, hiddenInForm: true, hidden: true },
            { key: "category", label: "Categoría", default: "wastewater", hiddenInForm: true, hidden: true },
            { key: "comments", label: "Comentarios", type: "textarea", full: true },
          ]}
        />
      </div>
    </div>
  );
}
